import {
  Archive,
  ArrowRight,
  Box,
  Database,
  Layers,
  Network,
  Zap,
} from 'lucide-react'
import { useMemo, type ReactNode } from 'react'
import { StatusIcon } from '@/components/sv/status-icon'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type {
  AppStatus,
  DatabaseStatus,
  StaticSiteStatus,
} from '@/graphql.types'
import type React from 'react'

type ServiceCardProps = {
  loading?: boolean
  disabled?: boolean
  type: 'app' | 'db' | 'staticSite' | 'objectStorage' | 'loadBalancer'
  name: ReactNode
  info?: ReactNode
  status?: AppStatus | DatabaseStatus | StaticSiteStatus | 'suspended'
  onClick?: () => void
}

export const ServiceCard = ({
  name,
  info,
  type,
  loading,
  disabled,
  status,
  onClick,
}: ServiceCardProps) => {
  const Icon = useMemo(() => {
    if (loading) {
      return Box
    }

    switch (type) {
      case 'app':
        return Layers
      case 'db':
        return Database
      case 'staticSite':
        return Zap
      case 'objectStorage':
        return Archive
      case 'loadBalancer':
        return Network
      default:
        return Box
    }
  }, [type, loading])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      (e.key === 'Enter' || e.key === ' ') &&
      onClick &&
      !loading &&
      !disabled
    ) {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <Card
      role="button"
      tabIndex={loading || disabled ? -1 : 0}
      onClick={loading || disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled}
      aria-busy={loading}
      className={cn(
        'py-0 overflow-hidden',
        'group/service',
        'transition-shadow hover:shadow-lg',
        'hover:cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        disabled || loading ? 'pointer-events-none' : ''
      )}
    >
      <div className="flex items-center justify-between">
        <div
          className={cn(
            'w-20 h-18 rounded-lg m-1',
            'grid place-items-center',
            'overflow-hidden',
            'relative',
            'bg-muted'
          )}
        >
          <div
            className={cn(
              'absolute inset-0',
              'bg-gradient-to-b from-orange-600 to-orange-300 dark:to-orange-500 dark:from-orange-700',
              'transition-opacity opacity-0 group-hover/service:opacity-90'
            )}
            style={{ gridArea: '1/1' }}
          />
          <Icon
            className={cn(
              'size-6 z-2',
              'transition-colors text-muted-foreground group-hover/service:text-white'
            )}
            style={{ gridArea: '1/1' }}
          />
        </div>
        <div className="pl-4 w-full flex items-center">
          <div className="w-full grid gap-1">
            <h3
              className={cn(
                'text-sm font-semibold',
                disabled ? 'text-muted-foreground' : ''
              )}
            >
              {loading ? <Skeleton className="w-[80%] h-4 mb-1" /> : name}
            </h3>
            <div className="w-full flex items-center">
              <div
                className={cn(
                  'text-muted-foreground text-sm w-full',
                  disabled ? 'opacity-50' : ''
                )}
              >
                {loading ? (
                  <Skeleton className="w-[50%] h-4 mt-1" />
                ) : (
                  <div className="flex items-center gap-1">
                    <StatusIcon status={status} />
                    {info}
                  </div>
                )}
              </div>
            </div>
          </div>
          <ArrowRight
            className={cn(
              'transition-opacity opacity-0 group-hover/service:opacity-100',
              'size-4 text-muted-foreground ml-auto mr-4'
            )}
          />
        </div>
      </div>
    </Card>
  )
}
